/* Wat de organisatie- en contactpagina delen: terug en vorige/volgende, secties,
 * een gegevenslijst, meldingen, pillen en de rechten van de gebruiker. */

import { useEffect, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertTriangle, Archive, ArrowLeft, ChevronLeft, ChevronRight, SearchX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import { buren, type LijstGeheugen } from '../data/geheugen';
import type { RelatieFout } from '../data/pagina';

/** Bewerken mag iedereen met CRM-toegang (zoals de policies), archiveren alleen beheerders.
 *  Buiten de AuthProvider (toetsen van de pagina): alleen lezen. */
export function useRechten(): { magBewerken: boolean; magBeheren: boolean } {
  try {
    const { userRole } = useAuth();
    const beheer = userRole === 'admin' || userRole === 'super_admin';
    return { magBewerken: beheer || userRole === 'crm_user', magBeheren: beheer };
  } catch {
    return { magBewerken: false, magBeheren: false };
  }
}

export function Navigatie({ id, geheugen, terug, pad, enkelvoud }: {
  id: string;
  geheugen: LijstGeheugen;
  terug: string;
  pad: (id: string) => string;
  /** 'organisatie' of 'contact', voor de knoppen. */
  enkelvoud: string;
}) {
  const navigate = useNavigate();
  const volgorde = geheugen.volgorde();
  const b = volgorde && volgorde.length > 1 ? buren(volgorde, id) : null;
  const vorigeLabel = `Vorige ${enkelvoud}`;
  const volgendeLabel = `Volgende ${enkelvoud}`;

  useEffect(() => {
    const toets = (e: KeyboardEvent) => {
      const doel = e.target as HTMLElement;
      if (doel.tagName === 'INPUT' || doel.tagName === 'TEXTAREA' || doel.isContentEditable || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === '[' && b?.vorige) navigate(pad(b.vorige));
      if (e.key === ']' && b?.volgende) navigate(pad(b.volgende));
    };
    window.addEventListener('keydown', toets);
    return () => window.removeEventListener('keydown', toets);
  }, [b?.vorige, b?.volgende, navigate, pad]);

  return (
    <div className="-ml-2 flex items-center gap-1">
      <Link to={geheugen.adres()} className="mr-1 inline-flex h-8 items-center gap-1.5 rounded-md px-2 text-sm text-muted-foreground hover:bg-muted hover:text-foreground">
        <ArrowLeft className="h-4 w-4" aria-hidden />
        {terug}
      </Link>
      {b && (
        <>
          <Button variant="ghost" size="icon" className="h-8 w-8" disabled={!b.vorige} onClick={() => b.vorige && navigate(pad(b.vorige))}
            aria-label={vorigeLabel} title={`${vorigeLabel} ( [ )`}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="min-w-[40px] text-center text-xs tabular-nums text-muted-foreground" title="Volgorde van je lijst">{b.positie} / {b.totaal}</span>
          <Button variant="ghost" size="icon" className="h-8 w-8" disabled={!b.volgende} onClick={() => b.volgende && navigate(pad(b.volgende))}
            aria-label={volgendeLabel} title={`${volgendeLabel} ( ] )`}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </>
      )}
    </div>
  );
}

export function Deel({ id, titel, aantal, actie, children }: { id: string; titel: string; aantal?: number; actie?: ReactNode; children: ReactNode }) {
  return (
    <section id={id} aria-labelledby={`relatie-${id}`} className="flex scroll-mt-14 flex-col gap-2.5">
      <div className="flex items-center justify-between gap-2">
        <h2 id={`relatie-${id}`} className="text-lg font-semibold">
          {titel}
          {aantal !== undefined && <span className="ml-2 text-base font-normal tabular-nums text-muted-foreground">{aantal}</span>}
        </h2>
        {actie}
      </div>
      {children}
    </section>
  );
}

/** Een lijst met rijen in een rand, zoals de rijen van de projectpagina. */
export function Rijen({ children, leeg }: { children: ReactNode[]; leeg: ReactNode }) {
  if (!children.length) return <p className="rounded-xl border border-dashed px-4 py-5 text-sm text-muted-foreground">{leeg}</p>;
  return <ul className="divide-y overflow-hidden rounded-xl border bg-background">{children}</ul>;
}

export function Rij({ naar, children, className, ...rest }: { naar?: string; children: ReactNode; className?: string } & Record<`data-${string}`, string>) {
  const inhoud = 'flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 text-sm';
  return (
    <li {...rest}>
      {naar ? (
        <Link to={naar} className={cn(inhoud, 'hover:bg-muted/60 focus-visible:bg-muted focus-visible:outline-none', className)}>{children}</Link>
      ) : (
        <div className={cn(inhoud, className)}>{children}</div>
      )}
    </li>
  );
}

export function Gegevenslijst({ rijen }: { rijen: [string, ReactNode][] }) {
  return (
    <dl className="m-0 grid grid-cols-1 gap-x-6 gap-y-2.5 rounded-xl border bg-background px-4 py-3 text-sm sm:grid-cols-[160px_minmax(0,1fr)]">
      {rijen.map(([label, waarde]) => (
        <div key={label} className="contents">
          <dt className="text-muted-foreground">{label}</dt>
          <dd className="m-0 min-w-0 break-words">{waarde}</dd>
        </div>
      ))}
    </dl>
  );
}

export function Pil({ className, children }: { className?: string; children: ReactNode }) {
  return <span className={cn('inline-flex shrink-0 items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold', className)}>{children}</span>;
}

export function Feit({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex shrink-0 flex-col border-l pl-4 first:border-l-0 first:pl-0">
      <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="m-0 text-[15px] font-semibold tabular-nums">{children}</dd>
    </div>
  );
}

/** Laden, niet gevonden, gearchiveerd of een fout: nooit een stil leeg scherm. */
export function PaginaMelding({ soort, fout, lijst, lijstLabel, opnieuw }: {
  soort: 'laden' | 'niet-gevonden' | 'gearchiveerd' | 'fout';
  fout?: RelatieFout;
  lijst: string;
  lijstLabel: string;
  opnieuw?: () => void;
}) {
  const titel = {
    laden: 'Laden…',
    'niet-gevonden': 'Niet gevonden',
    gearchiveerd: 'Gearchiveerd',
    fout: 'Kon niet worden opgehaald',
  }[soort];
  const icoon = soort === 'gearchiveerd' ? <Archive className="h-5 w-5" /> : soort === 'fout' ? <AlertTriangle className="h-5 w-5" /> : <SearchX className="h-5 w-5" />;
  return (
    <Card data-relatie-staat={soort} role={soort === 'laden' ? 'status' : 'alert'}>
      <CardContent className="p-6">
        {soort === 'laden' ? (
          <p className="text-sm text-muted-foreground">Laden…</p>
        ) : (
          <div className="flex items-start gap-3">
            <div className="mt-0.5 text-muted-foreground">{icoon}</div>
            <div className="min-w-0 space-y-2">
              <h1 className="text-lg font-semibold text-foreground">{titel}</h1>
              <p className="break-words text-sm text-muted-foreground">{fout?.message}</p>
              <div className="flex flex-wrap gap-2 pt-2">
                {opnieuw && <Button size="sm" onClick={opnieuw}>Opnieuw proberen</Button>}
                {soort === 'gearchiveerd' && <Button size="sm" asChild><Link to="/crm/archief">Naar het Archief</Link></Button>}
                <Button size="sm" variant="outline" asChild><Link to={lijst}>Naar {lijstLabel}</Link></Button>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
